import { ResourceContext } from '@seddi/core';
import type { IVector3 } from '@seddi/math-v2';
import type { Fabric } from '@seddi/scene';
import type { RecolorContext } from '@seddi/ui-recolor';
import type { IBabylonFabric } from './fabricTypes';
import { FabricLoader } from './fabricLoader';

export class FabricsContext extends ResourceContext<IBabylonFabric> {
  /** Fabric loader. */
  private readonly fabricLoader: FabricLoader;
  /** Recolor context */
  private readonly recolorContext?: RecolorContext;

  /**
   * Creates a new fabric resources context.
   * @param scene - Graphics context.
   */
  constructor(fabricLoader: FabricLoader, recolorContext?: RecolorContext) {
    super();
    this.fabricLoader = fabricLoader;
    this.recolorContext = recolorContext;
  }

  /**
   * Returns the fabric with the given ID, or undefined if the fabric has not been loaded.
   * @param fabricId - ID of the fabric to retrieve.
   * @param color - Fabric recolored variant.
   */
  public get(fabricId: string, color?: IVector3): IBabylonFabric | undefined {
    const fabricId_ = FabricLoader.GetFabricId(fabricId, color);
    return super.get(fabricId_);
  }

  /**
   * Loads the fabric.
   * If the fabric is already loaded, returns a resolved promise.
   * If the fabric is currently being loaded, returns a promise that will be resolved when the fabric finishes loading.
   * @param fabric - Fabric to load.
   * @param color - Fabric recolored variant.
   */
  public async loadEx(fabric: Fabric, color?: IVector3): Promise<void> {
    const fabricId = FabricLoader.GetFabricId(fabric.id, color);
    return super.load(
      fabricId,
      () => this.fabricLoader.loadAsync(fabric, color, this.recolorContext),
      (fabric: IBabylonFabric) => this.fabricLoader.unload(fabric, color, this.recolorContext),
    );
  }
}
